import SessionManager from './sessionManager';

// totalQuoteSteps = number of `quote_step_complete` buckets fired during the
// quote flow (i.e. the step_number of the last bucket). quote_results_view and
// thank_you_view step numbers are derived from this (totalQuoteSteps + 1 / +2).
const PRODUCT_CONFIG = {
  CarInsurance: {
    gtmProduct: 'car_insurance',
    totalQuoteSteps: 5,
    conversionType: 'branded_contact',
  },
  ReturningUser: {
    gtmProduct: 'car_insurance',
    totalQuoteSteps: 5,
    conversionType: 'branded_contact',
  },
  AdditionalDriver: {
    gtmProduct: 'car_insurance',
    totalQuoteSteps: 5,
    conversionType: 'branded_contact',
  },
  MotorcycleInsurance: {
    gtmProduct: 'motorbike_insurance',
    totalQuoteSteps: 5,
    conversionType: 'branded_contact',
  },
  AdditionalMotorCycleDriver: {
    gtmProduct: 'motorbike_insurance',
    totalQuoteSteps: 5,
    conversionType: 'branded_contact',
  },
  'Scratch&DentCover': {
    gtmProduct: 'scratch_and_dent_cover',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  'Tyre&RimCover': {
    gtmProduct: 'tyre_and_rim_cover',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  MotorWarranty: {
    gtmProduct: 'motor_warranty',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  ServicePlans: {
    gtmProduct: 'service_plans',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  VehicleTracking: {
    gtmProduct: 'car_tracking',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  MedicalAid: {
    gtmProduct: 'medical_aid',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  MedicalInsurance: {
    gtmProduct: 'medical_insurance',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  GapCover: {
    gtmProduct: 'gap_cover',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  LifeInsurance: {
    gtmProduct: 'life_insurance',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  FuneralInsurance: {
    gtmProduct: 'funeral_insurance',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  PetInsurance: {
    gtmProduct: 'pet_insurance',
    totalQuoteSteps: 5,
    conversionType: 'branded_contact',
  },
  TravelInsurance: {
    gtmProduct: 'travel_insurance',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  BuildingInsurance: {
    gtmProduct: 'buildings_insurance',
    totalQuoteSteps: 5,
    conversionType: 'branded_contact',
  },
  HomeContentsCover: {
    gtmProduct: 'home_contents_insurance',
    totalQuoteSteps: 5,
    conversionType: 'branded_contact',
  },
  BusinessInsurance: {
    gtmProduct: 'business_insurance',
    totalQuoteSteps: 5,
    conversionType: 'lead',
  },
  CommercialProperty: {
    gtmProduct: 'car_and_commercial_property_insurance',
    totalQuoteSteps: 5,
    conversionType: 'lead',
  },
  BusinessLegalCover: {
    gtmProduct: 'business_legal_cover',
    totalQuoteSteps: 5,
    conversionType: 'lead',
  },
  ProfessionalLiability: {
    gtmProduct: 'professional_liability',
    totalQuoteSteps: 5,
    conversionType: 'lead',
  },
  EmployeeBenefits: {
    gtmProduct: 'employee_benefits',
    totalQuoteSteps: 4,
    conversionType: 'callback_request',
  },
  FibreInternet: {
    gtmProduct: 'home_fibre',
    totalQuoteSteps: 4,
    conversionType: 'branded_contact',
  },
  PersonalLoans: {
    gtmProduct: 'personal_loans',
    totalQuoteSteps: 3,
    conversionType: 'branded_contact',
  },
  DebtCounselling: {
    gtmProduct: 'debt_counselling',
    totalQuoteSteps: 3,
    conversionType: 'branded_contact',
  },
};

function getProductConfig(product) {
  return PRODUCT_CONFIG[product];
}

export function toGtmProduct(product) {
  return getProductConfig(product)?.gtmProduct ?? product ?? '';
}

export function pushDataLayer(payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

// --- Enhanced conversions (Google Ads / GA4) -------------------------------

// Contact details land under different keys depending on the flow: the standard
// question engine stores `phone`/`email`, the business flows store
// `businessPersonPhone`/`businessPersonEmail`, and returning users come back
// from the API as `CellNumber`/`EmailAddress`.
const EMAIL_KEYS = [
  'email',
  'Email',
  'emailAddress',
  'EmailAddress',
  'businessPersonEmail',
];
const PHONE_KEYS = [
  'phone',
  'cellNumber',
  'CellNumber',
  'cell',
  'businessPersonPhone',
];

function firstValue(sources, keys) {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }
  return '';
}

function readStoredJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

// Google matches on a normalised address - trimmed and lowercased.
function normalizeEmail(email) {
  const value = String(email ?? '')
    .trim()
    .toLowerCase();
  return value.includes('@') ? value : '';
}

// Enhanced conversions expect E.164, so local SA numbers (0XX XXX XXXX) are
// converted to +27XXXXXXXXX. Anything that isn't a recognisable number is
// dropped rather than sent in a format Google can't match on.
function toE164(phone) {
  const raw = String(phone ?? '').trim();
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (raw.startsWith('+')) return `+${digits}`;
  if (digits.length === 11 && digits.startsWith('27')) return `+${digits}`;
  if (digits.length === 10 && digits.startsWith('0'))
    return `+27${digits.slice(1)}`;
  return '';
}

// Publishes the user's contact details for enhanced conversions. This has to
// land in the data layer *before* the conversion event, otherwise the Google
// tag reads an empty variable and sends no user data.
export function pushUserData(formValues) {
  const sources = [
    formValues,
    SessionManager.getFormValues(),
    readStoredJson('authSession'),
    readStoredJson('returnUserDetails'),
  ];

  const email = normalizeEmail(firstValue(sources, EMAIL_KEYS));
  const phone = toE164(firstValue(sources, PHONE_KEYS));

  // The `user_data` key is always pushed, even when empty, so details from an
  // earlier conversion in the same session can't be attributed to this one.
  pushDataLayer({
    user_data: {
      ...(email ? { email_address: email } : {}),
      ...(phone ? { phone_number: phone } : {}),
    },
  });

  return { email_address: email, phone_number: phone };
}

export function fireGtmSteps(gtm, product) {
  const steps = Array.isArray(gtm) ? gtm : [gtm];
  steps.forEach((step) => pushQuoteStepComplete({ product, ...step }));
}

export function pushQuoteStepComplete({ product, step_number, step_name }) {
  pushDataLayer({
    event: 'quote_step_complete',
    product: toGtmProduct(product),
    page_type: 'quote',
    quote_id: SessionManager.getSessionId(),
    step_number,
    step_name,
  });
}

export function pushQuoteResultsView({ product }) {
  const config = getProductConfig(product);
  const stepNumber = (config?.totalQuoteSteps ?? 4) + 1;

  pushDataLayer({
    event: 'quote_results_view',
    product: toGtmProduct(product),
    page_type: 'results',
    quote_id: SessionManager.getSessionId(),
    step_number: stepNumber,
    step_name: 'quote_results',
  });
}

export function pushThankYouView({
  product,
  partner_name,
  premium,
  conversion_type,
  formValues,
}) {
  // Enhanced conversions: must be pushed before the conversion event below.
  pushUserData(formValues);

  const config = getProductConfig(product);
  const isCallbackRequest =
    (conversion_type ?? config?.conversionType) === 'callback_request';
  // Employee Benefits has no Results page, so its conversion event lands one
  // step earlier than products that show quote results before converting.
  const stepNumber =
    (config?.totalQuoteSteps ?? 4) + (isCallbackRequest ? 1 : 2);

  pushDataLayer({
    event: 'thank_you_view',
    product: toGtmProduct(product),
    page_type: 'thank_you',
    quote_id: SessionManager.getSessionId(),
    step_number: stepNumber,
    step_name: isCallbackRequest ? 'callback_requested' : 'thank_you',
    partner_name: partner_name ?? '',
    ...(isCallbackRequest ? {} : { premium: premium ?? 0 }),
    conversion_type:
      conversion_type ?? config?.conversionType ?? 'branded_contact',
  });
}
